import React, { useState, useEffect, useCallback } from 'react';

// Introducer company/manager invite panel: send team invites by email and
// manage pending ones (copy link / resend / revoke). Mirrors the broker
// TeamInvitePanel but hits the /api/introducer/* invite endpoints and creates
// introducer-side members (manager/adviser). Self-contained — drop into the
// introducer team page.
const tok = () => localStorage.getItem('token');
const linkFor = (token) => `${window.location.origin}/introducer-team-invite/${token}`;

const btn = (bg, color) => ({ padding: '5px 10px', background: bg, color, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 });
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#4b5a6e', margin: '10px 0 4px' };
const inp = { width: '100%', padding: '9px 11px', border: '1px solid #cbd5e0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

// canChooseRole: company (and solo, who is promoted to company on first invite)
// may pick manager or adviser; a plain manager can only invite advisers.
export default function IntroducerTeamInvitePanel({ managers = [], canChooseRole = false }) {
  const [invites, setInvites] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'adviser', manager_id: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/introducer/invites', { headers: { Authorization: `Bearer ${tok()}` } });
      if (res.ok) { const d = await res.json(); setInvites(d.invites || []); }
    } catch (_) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 1600); } catch (_) {}
  };

  const submit = async (e) => {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      const body = { name: form.name.trim(), email: form.email.trim(), introducer_level: form.role };
      if (form.role === 'adviser' && form.manager_id) body.introducer_parent_id = form.manager_id;
      const res = await fetch('/api/introducer/invite-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.error || d.message || 'Could not send invite'); setBusy(false); return; }
      setLastLink({ email: body.email, link: d.acceptLink });
      setForm({ name: '', email: '', role: 'adviser', manager_id: '' });
      setShowModal(false);
      load();
    } catch (_) { setError('Network error'); }
    setBusy(false);
  };

  const resend = async (id) => {
    try {
      const res = await fetch(`/api/introducer/invites/${id}/resend`, { method: 'POST', headers: { Authorization: `Bearer ${tok()}` } });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.acceptLink) copy(d.acceptLink, `resend-${id}`);
    } catch (_) {}
    load();
  };
  const revoke = async (id) => {
    if (!window.confirm('Revoke this invite? The link will stop working.')) return;
    try { await fetch(`/api/introducer/invites/${id}/revoke`, { method: 'POST', headers: { Authorization: `Bearer ${tok()}` } }); } catch (_) {}
    load();
  };

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>
          Pending invites <span style={{ fontSize: 13, fontWeight: 400, color: '#a0aec0' }}>({invites.length})</span>
        </h2>
        <button onClick={() => { setError(''); setShowModal(true); }} style={{ ...btn('#0e1b2c', '#fff'), padding: '7px 14px', fontSize: 13 }}>
          ✉️ Invite teammate
        </button>
      </div>

      {lastLink && (
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: 14, marginBottom: 12 }}>
          <div style={{ fontWeight: 600, color: '#065f46', fontSize: 13, marginBottom: 6 }}>Invite sent to {lastLink.email}</div>
          <div style={{ fontSize: 12, color: '#047857', marginBottom: 8 }}>We emailed them a link. You can also share it directly:</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input readOnly value={lastLink.link} onFocus={e => e.target.select()} style={{ flex: 1, minWidth: 180, fontSize: 12, padding: '8px 10px', border: '1px solid #a7f3d0', borderRadius: 6, color: '#0e1b2c', background: '#fff' }} />
            <button onClick={() => copy(lastLink.link, 'last')} style={btn('#10b981', '#fff')}>{copied === 'last' ? 'Copied!' : 'Copy'}</button>
            <button onClick={() => setLastLink(null)} style={btn('#EDF2F7', '#374151')}>Dismiss</button>
          </div>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
        {invites.length === 0
          ? <div style={{ padding: '20px 16px', color: '#a0aec0', fontSize: 13 }}>No pending invites. Invite a teammate to give them a login.</div>
          : invites.map(inv => (
            <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #F7FAFC', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 600, color: '#0e1b2c', fontSize: 14 }}>
                  {inv.name}
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '1px 7px', borderRadius: 9999, marginLeft: 6 }}>
                    {inv.role}{inv.manager_name ? ` · under ${inv.manager_name}` : ''}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#718096' }}>{inv.email} · expires {new Date(inv.expires_at).toLocaleDateString('en-GB')}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => copy(linkFor(inv.token), `copy-${inv.id}`)} style={btn('#e0f2fe', '#075985')}>{copied === `copy-${inv.id}` ? 'Copied!' : 'Copy link'}</button>
                <button onClick={() => resend(inv.id)} style={btn('#EDF2F7', '#374151')}>{copied === `resend-${inv.id}` ? 'Link copied' : 'Resend'}</button>
                <button onClick={() => revoke(inv.id)} style={btn('#fee2e2', '#991b1b')}>Revoke</button>
              </div>
            </div>
          ))
        }
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={() => setShowModal(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={submit} style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 420 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#0e1b2c' }}>Invite a teammate</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#718096' }}>They'll get an email to set their own password and sign in.</p>
            {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <label style={lbl}>Full name</label>
            <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inp} />
            <label style={lbl}>Email</label>
            <input required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inp} />
            <label style={lbl}>Role</label>
            {canChooseRole ? (
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={inp}>
                <option value="adviser">Adviser</option>
                <option value="manager">Manager</option>
              </select>
            ) : (
              <div style={{ ...inp, background: '#f1f5f9', color: '#9ca3af' }}>Adviser</div>
            )}
            {form.role === 'adviser' && managers.length > 0 && (
              <>
                <label style={lbl}>Reports to (manager)</label>
                <select value={form.manager_id} onChange={e => setForm(f => ({ ...f, manager_id: e.target.value }))} style={inp}>
                  <option value="">— No manager —</option>
                  {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: '10px', background: '#EDF2F7', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button type="submit" disabled={busy} style={{ flex: 1, padding: '10px', background: busy ? '#a7f3d0' : '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>{busy ? 'Sending…' : 'Send invite'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
